import type { TusUploadState } from "./upload"

export class UploadFinalizeError extends Error {
	tusId: string
	constructor(tusId: string, message: string) {
		super(message)
		this.tusId = tusId
	}
}

const POLL_INTERVAL_MS = 1000
/** Never wait less than this, whatever the file size */
const MIN_WAIT_MS = 2 * 60 * 1000
/** Budget for the server-side copy, sized to a slow single-part copy so a real stall is what times out */
const ASSUMED_COPY_BYTES_PER_SECOND = 10 * 1024 * 1024

/** How long to wait for the API to finish staging an upload of `sizeBytes`. */
export function uploadFinalizeBudgetMs(sizeBytes: number | null | undefined): number {
	if (!sizeBytes || sizeBytes <= 0) return MIN_WAIT_MS
	return Math.max(MIN_WAIT_MS, (sizeBytes / ASSUMED_COPY_BYTES_PER_SECOND) * 1000 + 30_000)
}

/**
 * Poll `lookup` until the API reports the upload's bytes are in place, then return the
 * state (with the entry id). Throws UploadFinalizeError when the API reports a failure,
 * when the upload is unknown to the API, or when the budget runs out.
 */
export async function waitForUploadReady(
	tusId: string,
	lookup: (tusId: string) => Promise<TusUploadState>,
	options: { sizeBytes?: number | null; budgetMs?: number; pollIntervalMs?: number } = {}
): Promise<TusUploadState> {
	const budgetMs = options.budgetMs ?? uploadFinalizeBudgetMs(options.sizeBytes)
	const pollIntervalMs = options.pollIntervalMs ?? POLL_INTERVAL_MS
	const deadline = Date.now() + budgetMs
	let unknownStreak = 0

	while (Date.now() < deadline) {
		let state: TusUploadState | null = null
		try {
			state = await lookup(tusId)
		} catch (err) {
			// The hook may not have written the record yet; a few 404s in a row are expected right after completion.
			unknownStreak += 1
			if (unknownStreak >= 15) {
				throw new UploadFinalizeError(
					tusId,
					`The upload finished but the server has no record of it (tus id ${tusId}). ${String(err)}`
				)
			}
		}

		if (state) {
			unknownStreak = 0
			if (state.state === "ready") return state
			if (state.state === "failed") {
				throw new UploadFinalizeError(
					tusId,
					`The server could not finalize the upload (tus id ${tusId}, entry ${state.entry_id ?? "none"}): ${state.error ?? "no reason given"}`
				)
			}
		}

		await new Promise((res) => setTimeout(res, pollIntervalMs))
	}

	throw new UploadFinalizeError(
		tusId,
		`Timed out after ${Math.round(budgetMs / 1000)}s waiting for the server to finalize the upload (tus id ${tusId})`
	)
}
